import type { Id } from '@hq/utils';
import { toQuery, type HttpClient } from '../client';
import type {
  CreateDeliveryRequest,
  DeliveryDetail,
  DeliveryListQuery,
  DeliveryRow,
  SignDeliveryRequest,
  SignDetail,
  SignListQuery,
  SignResult,
  SignRow
} from '../types/delivery';
import type { PageResult } from '../types/result';

const BASE = '/wl/delivery';
const SIGN_BASE = '/wl/sign';

/** 配送与签收（Hq.Api）。生成配送单为直送模式：来源申领单。 */
export function createDeliveryApi(http: HttpClient) {
  return {
    list(query: DeliveryListQuery = {}): Promise<PageResult<DeliveryRow>> {
      return http.request<PageResult<DeliveryRow>>({ path: BASE, query: toQuery(query) });
    },

    /**
     * 待签收配送单：在途（DELIVERING）+ 部分签收待补签（PART_SIGNED）。
     * 后端 `delivery_status` 是单值等值过滤（`@status='' OR delivery_status=@status`），
     * 而 PART_SIGNED 的单据仍可继续补签（DeliveryService 只拦截 SIGNED/REJECTED/CANCELED/LOST），
     * 只查 DELIVERING 会让部分签收的单据在移动端彻底消失。这里按两个状态分别拉取再合并去重。
     */
    async listSignable(query: DeliveryListQuery = {}): Promise<PageResult<DeliveryRow>> {
      const size = query.size ?? 20;
      const [delivering, partSigned] = await Promise.all([
        http.request<PageResult<DeliveryRow>>({
          path: BASE,
          query: toQuery({ ...query, size, deliveryStatus: 'DELIVERING' })
        }),
        http.request<PageResult<DeliveryRow>>({
          path: BASE,
          query: toQuery({ ...query, size, deliveryStatus: 'PART_SIGNED' })
        })
      ]);

      const byId = new Map<string, DeliveryRow>();
      for (const row of [...delivering.items, ...partSigned.items]) byId.set(row.id, row);

      const items = [...byId.values()]
        .sort((a, b) => (a.id > b.id ? -1 : a.id < b.id ? 1 : 0))
        .slice(0, size);

      return {
        items,
        nextLastId: items[items.length - 1]?.id ?? null,
        hasMore: delivering.hasMore || partSigned.hasMore
      };
    },

    detail(id: Id): Promise<DeliveryDetail> {
      return http.request<DeliveryDetail>({ path: `${BASE}/${id}` });
    },

    /** 从申领单生成配送单（幂等：已存在则返回已有单）。 */
    createFromApply(body: CreateDeliveryRequest): Promise<DeliveryDetail> {
      return http.request<DeliveryDetail>({ path: BASE, method: 'POST', body });
    },

    /**
     * 扫码签收：写签收单与明细/影像，回写配送单与申领单状态。
     * `requestId` 供离线重放复用同一个幂等键（不传则每次调用新生成）。
     */
    sign(id: Id, body: SignDeliveryRequest, requestId?: string): Promise<SignResult> {
      return http.request<SignResult>({ path: `${BASE}/${id}/sign`, method: 'POST', body, requestId });
    },

    /** 签收记录查询（医院端现场核验 / 电子签名影像取证）。 */
    listSigns(query: SignListQuery = {}): Promise<PageResult<SignRow>> {
      return http.request<PageResult<SignRow>>({ path: SIGN_BASE, query: toQuery(query) });
    },

    /** 签收单详情（含现场影像）。 */
    signDetail(id: Id): Promise<SignDetail> {
      return http.request<SignDetail>({ path: `${SIGN_BASE}/${id}` });
    }
  };
}

export type DeliveryApi = ReturnType<typeof createDeliveryApi>;
